import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import editorService from "./editorService";
import {
  acceptAssociatedEditorTypes,
  addFileToDecisionTypes,
  addFileToTechnicalReviewTypes,
  applyTechnicalReviewTypes,
  assignAssociatedEditorTypes,
  assignReviewerTypes,
  DeleteFileFromDecisionTypes,
  editorArticleTypes,
  editorTypes,
  emitVeredictTypes,
  getReviewFileActor,
  historyTypes,
  rejectAssociatedEditorTypes,
  ServiceEditortypes,
  SuccessType,
} from "./editorTypes";

interface editorState {
  editorArticle: editorArticleTypes | null;
  articleHistory: historyTypes[] | null;
  editorArticles: editorArticleTypes[];
  editor: editorTypes | null;
  editors: editorTypes[] | null;
  associatedEditors: editorTypes[] | null;
  loading: boolean;
  error: any | null;
}

const initialState: editorState = {
  editorArticle: null,
  articleHistory: null,
  editorArticles: [],
  editor: null,
  editors: null,
  associatedEditors: null,
  loading: false,
  error: null,
};

export const createEditor = createAsyncThunk<
  SuccessType,
  editorTypes,
  { rejectValue: string }
>("editor/createEditor", async (data: editorTypes, { rejectWithValue }) => {
  try {
    const response = await editorService.createEditor(data);
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || error.message || "Error desconocido";
    return rejectWithValue(errorMessage);
  }
});

export const editEditor = createAsyncThunk<
  SuccessType,
  { data: editorTypes; slug: string },
  { rejectValue: string }
>(
  "reviewer/editEditor",
  async (
    { data, slug }: { data: editorTypes; slug: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await editorService.editEditor({ data, slug });
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getArticlesByStatus = createAsyncThunk<
  editorArticleTypes[],
  number[],
  { rejectValue: string }
>("editor/getArticlesByStatus", async (data: number[], { rejectWithValue }) => {
  try {
    const response = await editorService.getArticlesByStatus(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getArticlesInProduction = createAsyncThunk<
  editorArticleTypes[],
  string,
  { rejectValue: string }
>(
  "editor/getArticlesInProduction",
  async (volumen_slug: string, { rejectWithValue }) => {
    try {
      const response =
        await editorService.getArticlesInProduction(volumen_slug);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const getEditors = createAsyncThunk<
  editorTypes[],
  void,
  { rejectValue: string }
>("editor/getEditors", async (_, { rejectWithValue }) => {
  try {
    const response = await editorService.getEditors();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getArticlesHistory = createAsyncThunk<
  historyTypes[],
  string,
  { rejectValue: string }
>("editor/getArticlesHistory", async (slug: string, { rejectWithValue }) => {
  try {
    const response = await editorService.getArticlesHistory(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const assignAssociatedEditor = createAsyncThunk<
  SuccessType,
  assignAssociatedEditorTypes,
  { rejectValue: string }
>(
  "editor/assignAssociatedEditor",
  async (data: assignAssociatedEditorTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.assignAssociatedEditor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const applyTechnicalReview = createAsyncThunk<
  SuccessType,
  applyTechnicalReviewTypes,
  { rejectValue: string }
>(
  "editor/applyTechnicalReview",
  async (technicalReview: applyTechnicalReviewTypes, { rejectWithValue }) => {
    try {
      const response =
        await editorService.applyTechnicalReview(technicalReview);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const assignReviewer = createAsyncThunk<
  SuccessType,
  assignReviewerTypes,
  { rejectValue: string }
>(
  "editor/assignReviewer",
  async (reviewerAssignation: assignReviewerTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.assignReviewer(reviewerAssignation);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al asignar el revisor",
      );
    }
  },
);

export const sendToPublication = createAsyncThunk<
  SuccessType,
  { slug: string; comment: string },
  { rejectValue: string }
>(
  "editor/sendToPublication",
  async (data: { slug: string; comment: string }, { rejectWithValue }) => {
    try {
      const response = await editorService.sendToPublication(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const sendReminderToAuthor = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>(
  "editor/sendReminderToAuthor",
  async (article_slug: string, { rejectWithValue }) => {
    try {
      const response = await editorService.sendReminderToAuthor(article_slug);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const emitVeredict = createAsyncThunk(
  "editor/emitVeredict",
  async (veredict: emitVeredictTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.emitVeredict(veredict);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const changeStatus = createAsyncThunk<
  SuccessType,
  ServiceEditortypes,
  { rejectValue: string }
>(
  "editor/changeStatus",
  async (data: ServiceEditortypes, { rejectWithValue }) => {
    try {
      const response = await editorService.changesStatus(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getAssociatedEditors = createAsyncThunk<
  editorTypes[],
  void,
  { rejectValue: string }
>("editor/getAssociatedEditors", async (_, { rejectWithValue }) => {
  try {
    const response = await editorService.getAssociatedEditors();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const acceptAssociatedEditor = createAsyncThunk<
  SuccessType,
  acceptAssociatedEditorTypes,
  { rejectValue: string }
>(
  "editor/acceptAssociatedEditor",
  async (acceptationData: acceptAssociatedEditorTypes, { rejectWithValue }) => {
    try {
      const response =
        await editorService.acceptAssociatedEditor(acceptationData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const rejectAssociatedEditor = createAsyncThunk<
  SuccessType,
  rejectAssociatedEditorTypes,
  { rejectValue: string }
>(
  "editor/rejectAssociatedEditor",
  async (rejectionData: rejectAssociatedEditorTypes, { rejectWithValue }) => {
    try {
      const response =
        await editorService.rejectAssociatedEditor(rejectionData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getEditorArticlesByVolume = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>(
  "editor/getEditorArticlesByVolume",
  async (volume_slug: string, { rejectWithValue }) => {
    try {
      const articles =
        await editorService.getEditorArticlesByVolume(volume_slug);
      return articles;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addFileToTechnicalReview = createAsyncThunk<
  SuccessType,
  addFileToTechnicalReviewTypes,
  { rejectValue: string }
>(
  "editor/addFileToTechnicalReview",
  async (data: addFileToTechnicalReviewTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.addFileToTechnicalReview(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const deleteEditor = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("author/deleteEditor", async (slug: string, { rejectWithValue }) => {
  try {
    const response = await editorService.deleteEditor(slug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addFileToDecisionAssocEditor = createAsyncThunk<
  SuccessType,
  addFileToDecisionTypes,
  { rejectValue: string }
>(
  "assoc_editor/addFileToDecision",
  async (data: addFileToDecisionTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.addFileToDecisionAssocEditor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addFileToDecisionEditor = createAsyncThunk<
  SuccessType,
  addFileToDecisionTypes,
  { rejectValue: string }
>(
  "editor/addFileToDecision",
  async (data: addFileToDecisionTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.addFileToDecisionEditor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getReviewFileReviewer = createAsyncThunk<
  string,
  getReviewFileActor,
  { rejectValue: string }
>(
  "editor/getReviewFileReviewer",
  async (payload: getReviewFileActor, { rejectWithValue }) => {
    try {
      const response = await editorService.getReviewFileReviewer(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getReviewFileAssocEditor = createAsyncThunk<
  string,
  getReviewFileActor,
  { rejectValue: string }
>(
  "editor/getReviewFileAssocEditor",
  async (payload: getReviewFileActor, { rejectWithValue }) => {
    try {
      const response = await editorService.getReviewFileAssocEditor(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const deleteFileFromDecision = createAsyncThunk<
  SuccessType,
  DeleteFileFromDecisionTypes,
  { rejectValue: string }
>(
  "editor/deleteFileFromDecision",
  async (data: DeleteFileFromDecisionTypes, { rejectWithValue }) => {
    try {
      const response = await editorService.deleteFileFromDecision(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

// SIN USOS
export const getEditorReviewBySlug = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  "articles/getEditorReviewBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      const article = await editorService.getEditorReviewBySlug(slug);
      return article;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getAssociatedEditorArticlesByStatus = createAsyncThunk<
  editorArticleTypes[],
  number[],
  { rejectValue: string }
>(
  "editor/getAssociatedEditorArticlesByStatus",
  async (data: number[], { rejectWithValue }) => {
    try {
      const response =
        await editorService.getAssociatedEditorArticlesByStatus(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const getAssociatedEditorReviewBySlug = createAsyncThunk<
  editorTypes[],
  string,
  { rejectValue: string }
>(
  "editor/getAssociatedEditorReviewBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      const editors = await editorService.getAssociatedEditorReviewBySlug(slug);
      return editors;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getPendingReviewsAssocEd = createAsyncThunk<
  editorArticleTypes[],
  void,
  { rejectValue: string }
>("editor/getPendingReviewsAssocEd", async (_, { rejectWithValue }) => {
  try {
    const response = await editorService.getPendingReviewsAssocEd();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const getCompletedArticlesAssocEditor = createAsyncThunk<
  editorArticleTypes[],
  void,
  { rejectValue: string }
>("editor/getCompletedArticlesAssocEditor", async (_, { rejectWithValue }) => {
  try {
    const response = await editorService.getCompletedArticlesAssocEditor();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const reloadOpenSearch = createAsyncThunk<
  { status: string; publication: string },
  string,
  { rejectValue: string }
>("editor/reloadOpenSearch", async (articleSlug, { rejectWithValue }) => {
  try {
    const response = await editorService.reloadOpenSearch(articleSlug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

export const reloadVolumeOpenSearch = createAsyncThunk<
  { status: string; publication: string },
  string,
  { rejectValue: string }
>("editor/reloadVolumeOpenSearch", async (volumeSlug, { rejectWithValue }) => {
  try {
    const response = await editorService.reloadVolumeOpenSearch(volumeSlug);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Error desconocido");
  }
});

const editorSlice = createSlice({
  name: "editor",
  initialState,
  reducers: {
    resetEditorArticles: (state) => {
      state.editorArticles = [];
      state.editorArticle = null;
    },
  },
  extraReducers: (builder) => {
    // createEditor
    builder.addCase(createEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createEditor.fulfilled, (state, action) => {
      state.loading = false;
      state.editor = action.payload as any;
    });
    builder.addCase(createEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // editEditor
    builder.addCase(editEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(editEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(editEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getArticlesByStatus: Limpia al iniciar, rellena al terminar
    builder.addCase(getArticlesByStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.editorArticles = [];
    });
    builder.addCase(getArticlesByStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.editorArticles = action.payload;
    });
    builder.addCase(getArticlesByStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getAssociatedEditorArticlesByStatus
    builder.addCase(getAssociatedEditorArticlesByStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getAssociatedEditorArticlesByStatus.fulfilled,
      (state, action) => {
        state.loading = false;
        state.editorArticles = action.payload as editorArticleTypes[];
      },
    );
    builder.addCase(
      getAssociatedEditorArticlesByStatus.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error desconocido";
      },
    );

    // getPendingReviewsAssocEd
    builder.addCase(getPendingReviewsAssocEd.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPendingReviewsAssocEd.fulfilled, (state, action) => {
      state.loading = false;
      state.editorArticles = action.payload as editorArticleTypes[];
    });
    builder.addCase(getPendingReviewsAssocEd.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getCompletedArticlesAssocEditor
    builder.addCase(getCompletedArticlesAssocEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getCompletedArticlesAssocEditor.fulfilled,
      (state, action) => {
        state.loading = false;
        state.editorArticles = action.payload as editorArticleTypes[];
      },
    );
    builder.addCase(
      getCompletedArticlesAssocEditor.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error desconocido";
      },
    );

    // getEditorArticlesByVolume
    builder.addCase(getEditorArticlesByVolume.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEditorArticlesByVolume.fulfilled, (state, action) => {
      state.loading = false;
      state.editorArticles = action.payload as unknown as editorArticleTypes[];
    });
    builder.addCase(getEditorArticlesByVolume.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getEditors
    builder.addCase(getEditors.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEditors.fulfilled, (state, action) => {
      state.loading = false;
      state.editors = action.payload;
    });
    builder.addCase(getEditors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getAssociatedEditors
    builder.addCase(getAssociatedEditors.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAssociatedEditors.fulfilled, (state, action) => {
      state.loading = false;
      state.associatedEditors = action.payload;
    });
    builder.addCase(getAssociatedEditors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getAssociatedEditorReviewBySlug
    builder.addCase(getAssociatedEditorReviewBySlug.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getAssociatedEditorReviewBySlug.fulfilled,
      (state, action) => {
        state.loading = false;
        state.associatedEditors = action.payload;
      },
    );
    builder.addCase(
      getAssociatedEditorReviewBySlug.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error desconocido";
      },
    );

    // getArticlesHistory: Limpia al iniciar y si falla, rellena al terminar
    builder.addCase(getArticlesHistory.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.articleHistory = null;
    });
    builder.addCase(getArticlesHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.articleHistory = action.payload;
    });
    builder.addCase(getArticlesHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
      state.articleHistory = null;
    });

    // getEditorReviewBySlug
    builder.addCase(getEditorReviewBySlug.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEditorReviewBySlug.fulfilled, (state, action) => {
      state.loading = false;
      state.editorArticle = action.payload as editorArticleTypes;
    });
    builder.addCase(getEditorReviewBySlug.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // assignAssociatedEditor
    builder.addCase(assignAssociatedEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(assignAssociatedEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(assignAssociatedEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // applyTechnicalReview
    builder.addCase(applyTechnicalReview.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(applyTechnicalReview.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(applyTechnicalReview.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // assignReviewer
    builder.addCase(assignReviewer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(assignReviewer.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(assignReviewer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // sendToPublication
    builder.addCase(sendToPublication.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendToPublication.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendToPublication.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // sendReminderToAuthor
    builder.addCase(sendReminderToAuthor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendReminderToAuthor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendReminderToAuthor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // // emitVeredict
    // builder.addCase(emitVeredict.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    // });
    // builder.addCase(emitVeredict.fulfilled, (state) => {
    //     state.loading = false;
    // });
    // builder.addCase(emitVeredict.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload as string || "Error desconocido";
    // });

    // changeStatus
    builder.addCase(changeStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(changeStatus.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(changeStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // acceptAssociatedEditor
    builder.addCase(acceptAssociatedEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(acceptAssociatedEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(acceptAssociatedEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // rejectAssociatedEditor
    builder.addCase(rejectAssociatedEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(rejectAssociatedEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rejectAssociatedEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // // addFileToTechnicalReview
    // builder.addCase(addFileToTechnicalReview.pending, (state) => {
    //     state.loading = true;
    //     state.error = null;
    // });
    // builder.addCase(addFileToTechnicalReview.fulfilled, (state) => {
    //     state.loading = false;
    // });
    // builder.addCase(addFileToTechnicalReview.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload || "Error desconocido";
    // });

    // deleteEditor
    builder.addCase(deleteEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // addFileToDecision
    builder.addCase(addFileToDecisionAssocEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addFileToDecisionAssocEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addFileToDecisionAssocEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // addFileToDecision
    builder.addCase(addFileToDecisionEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addFileToDecisionEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addFileToDecisionEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getReviewFileReviewer
    builder.addCase(getReviewFileReviewer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getReviewFileReviewer.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(getReviewFileReviewer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // getReviewFileAssocEditor
    builder.addCase(getReviewFileAssocEditor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getReviewFileAssocEditor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(getReviewFileAssocEditor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });

    // deleteFileFromDecision
    builder.addCase(deleteFileFromDecision.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteFileFromDecision.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteFileFromDecision.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });
    // getArticlesInProduction
    builder.addCase(getArticlesInProduction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getArticlesInProduction.fulfilled, (state, action) => {
      state.loading = false;
      state.editorArticles = action.payload as editorArticleTypes[];
    });
    builder.addCase(getArticlesInProduction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Error desconocido";
    });
  },
});
export const { resetEditorArticles } = editorSlice.actions;
export default editorSlice.reducer;

export const updateReviewVisibility = createAsyncThunk<
  SuccessType,
  any,
  { rejectValue: string }
>("editor/updateReviewVisibility", async (data, { rejectWithValue }) => {
  try {
    const response = await editorService.updateReviewVisibility(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});
